/**
 * EvidenceRecoveryQueue — editorial evidence-recovery workflow.
 *
 * Every BROKEN/REMOVED source exposes "Recover Evidence": a Wayback Machine
 * search, then an explicit editor decision (replace citation / attach
 * archived reference / mark for review / keep unresolved). Nothing is ever
 * replaced silently; every decision is audited with editor + reason.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Archive, Loader2, ExternalLink, ShieldCheck, AlertTriangle,
  History, SearchCheck, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { adminRecoveryQueue, adminRecoveryCheck, adminRecoveryApply } from "../../lib/api";

const btn = "inline-flex items-center gap-1 px-2 py-1 border font-mono text-[8px] uppercase tracking-[0.18em] transition-colors disabled:opacity-40";

const fmtTs = (ts) => {
  if (!ts) return "—";
  const s = String(ts);
  return s.length >= 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : s;
};

const CONF_LABEL = {
  very_high: "Very High", high: "High", moderate: "Moderate", low: "Low",
};

const RecoveryItem = ({ item, token, userToken, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [checked, setChecked] = useState(
    item.recovery_checked_at || item.archived_available !== null ? {
      available: item.archived_available,
      archived_url: item.archived_snapshot,
      archive_timestamp: item.archived_timestamp,
      recovery_confidence: item.recovery_confidence,
      captured_before_break: item.captured_before_break,
      verified: item.archived_verified,
      original_url: item.original_url || item.url,
    } : null);
  const [reason, setReason] = useState("");

  const check = async () => {
    setBusy(true);
    try {
      const r = await adminRecoveryCheck(token, item.slug, item.url, userToken);
      setChecked(r);
      toast[r.available ? "success" : "info"](
        r.available ? "Archived copy located" : "No archived snapshot could be located.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Wayback lookup failed"); }
    finally { setBusy(false); }
  };

  const apply = async (action) => {
    if (!reason.trim()) { toast.error("A reason is required — every recovery is audited."); return; }
    setBusy(true);
    try {
      await adminRecoveryApply(token,
        { slug: item.slug, url: item.url, action, reason: reason.trim() }, userToken);
      toast.success(
        action === "replace" ? "Citation replaced with archived copy — audit logged"
          : action === "attach" ? "Archived reference attached — original citation kept"
          : action === "mark_review" ? "Marked for editorial review"
          : "Kept unresolved — decision recorded");
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#2D2A26] bg-[#131313] p-3"
         data-testid={`recovery-item-${item.slug}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.2em] px-1.5 py-0.5 border border-[#D92D20] text-[#D92D20]">
          {item.status}
        </span>
        <span className="text-[#E4DFD3] text-sm">{item.source_title || item.url}</span>
        <span className="font-mono text-[9px] text-[#7B7168]">· {item.topic_title}</span>
      </div>
      <div className="font-mono text-[9px] text-[#7B7168] break-all mt-1">{item.url}</div>

      {!checked && (
        <button type="button" disabled={busy} onClick={check}
                data-testid={`recover-evidence-btn-${item.slug}`}
                className={`${btn} mt-2 border-[#9FA8D4] text-[#9FA8D4] hover:bg-[#9FA8D4] hover:text-[#0a0907]`}>
          {busy ? <Loader2 size={9} className="animate-spin" /> : <SearchCheck size={9} />}
          Recover Evidence
        </button>
      )}

      {checked && checked.available && (
        <div className="mt-2 border border-[#9FA8D4] bg-[#101018] p-2.5"
             data-testid={`recovery-available-${item.slug}`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#9FA8D4]">
            Archived copy available
            {checked.recovery_confidence && ` · confidence: ${CONF_LABEL[checked.recovery_confidence] || checked.recovery_confidence}`}
            {checked.archive_timestamp && ` · snapshot ${fmtTs(checked.archive_timestamp)}`}
          </div>
          {checked.captured_before_break === true && (
            <div className="mt-1 inline-block px-1.5 py-0.5 border border-[#3FB950] font-mono text-[8px] uppercase tracking-[0.2em] text-[#3FB950]"
                 data-testid={`recovery-prebreak-${item.slug}`}>
              Captured before disappearance
            </div>
          )}
          {checked.captured_before_break === false && (
            <div className="mt-1 inline-block px-1.5 py-0.5 border border-[#D9A56C] font-mono text-[8px] uppercase tracking-[0.2em] text-[#D9A56C]">
              Captured after disappearance — verify content before citing
            </div>
          )}
          {checked.verified === false && (
            <div className="mt-1 inline-block px-1.5 py-0.5 border border-[#D9A56C] font-mono text-[8px] uppercase tracking-[0.2em] text-[#D9A56C]">
              Content not auto-verified — inspect manually
            </div>
          )}
          <div className="font-mono text-[9px] text-[#7B7168] break-all mt-1">
            Original: {checked.original_url || item.url}
          </div>
          <div className="font-mono text-[9px] text-[#A39E93] break-all">
            Archive: {checked.archived_url}
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Reason (required — recorded in the audit trail)"
                 data-testid={`recovery-reason-${item.slug}`}
                 className="mt-2 w-full bg-[#0a0907] border border-[#2D2A26] px-2 py-1.5 font-mono text-[10px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#9FA8D4]" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <a href={checked.archived_url} target="_blank" rel="noreferrer"
               data-testid={`recovery-view-btn-${item.slug}`}
               className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#9FA8D4] hover:text-[#9FA8D4]`}>
              <ExternalLink size={9} /> View archived copy
            </a>
            <button type="button" disabled={busy} onClick={check}
                    data-testid={`recovery-reverify-btn-${item.slug}`}
                    className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#9FA8D4] hover:text-[#9FA8D4]`}>
              {busy ? <Loader2 size={9} className="animate-spin" /> : <SearchCheck size={9} />}
              Re-verify snapshot
            </button>
            <button type="button" disabled={busy} onClick={() => apply("replace")}
                    data-testid={`recovery-replace-btn-${item.slug}`}
                    className={`${btn} border-[#3FB950] text-[#3FB950] hover:bg-[#3FB950] hover:text-[#0a0907]`}>
              <ShieldCheck size={9} /> Replace citation
            </button>
            <button type="button" disabled={busy} onClick={() => apply("attach")}
                    data-testid={`recovery-attach-btn-${item.slug}`}
                    className={`${btn} border-[#9FA8D4] text-[#9FA8D4] hover:bg-[#9FA8D4] hover:text-[#0a0907]`}>
              <Archive size={9} /> Keep original + attach archive
            </button>
          </div>
        </div>
      )}

      {checked && !checked.available && (
        <div className="mt-2 border border-[#454038] bg-[#12110e] p-2.5"
             data-testid={`recovery-none-${item.slug}`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#D9A56C]">
            No archived snapshot could be located.
          </div>
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Reason (required — recorded in the audit trail)"
                 data-testid={`recovery-reason-${item.slug}`}
                 className="mt-2 w-full bg-[#0a0907] border border-[#2D2A26] px-2 py-1.5 font-mono text-[10px] text-[#E4DFD3] placeholder:text-[#4A463F] focus:outline-none focus:border-[#D9A56C]" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button type="button" disabled={busy} onClick={() => apply("mark_review")}
                    data-testid={`recovery-review-btn-${item.slug}`}
                    className={`${btn} border-[#D9A56C] text-[#D9A56C] hover:bg-[#D9A56C] hover:text-[#0a0907]`}>
              <AlertTriangle size={9} /> Mark for editorial review
            </button>
            <button type="button" disabled={busy} onClick={() => apply("keep_unresolved")}
                    data-testid={`recovery-keep-btn-${item.slug}`}
                    className={`${btn} border-[#454038] text-[#A39E93] hover:border-[#7B7168]`}>
              <XCircle size={9} /> Keep unresolved
            </button>
          </div>
          <div className="mt-1.5 font-mono text-[8px] text-[#7B7168]">
            Replacement sources can be proposed from the per-source tools below.
          </div>
        </div>
      )}
    </div>
  );
};

const Group = ({ title, hint, items, children, testid }) => {
  if (!items?.length) return null;
  return (
    <details className="mt-4" data-testid={testid} open={testid === "recovery-group-available"}>
      <summary className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] cursor-pointer">
        {title} ({items.length}){hint && <span className="text-[#7B7168] normal-case tracking-normal"> — {hint}</span>}
      </summary>
      <div className="space-y-2 mt-2">{children}</div>
    </details>
  );
};

export const EvidenceRecoveryQueue = ({ token, userToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setData(await adminRecoveryQueue(token, userToken)); }
    catch { toast.error("Could not load the Evidence Recovery queue"); }
    finally { setLoading(false); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="border border-[#2D2A26] p-4 mb-6 font-mono text-xs text-[#A39E93] flex items-center gap-2">
        <Loader2 size={13} className="animate-spin" /> Loading Evidence Recovery queue…
      </div>
    );
  }
  if (!data) return null;
  const actionable = (data.available?.length || 0) + (data.unchecked?.length || 0)
    + (data.no_archive?.length || 0);

  return (
    <div className="border border-[#2D2A26] bg-[#0F0F0F] p-4 mb-6"
         data-testid="evidence-recovery-queue">
      <div className="flex items-center gap-2">
        <History size={14} className="text-[#9FA8D4]" />
        <h2 className="font-display text-lg text-[#E4DFD3]">Evidence Recovery Queue</h2>
        <span className="font-mono text-[9px] text-[#7B7168]">
          {actionable ? `${actionable} source${actionable === 1 ? "" : "s"} awaiting an editorial decision`
            : "no broken evidence awaiting recovery"}
        </span>
      </div>
      <p className="font-mono text-[9px] text-[#7B7168] mt-1">
        The Archive never hides history — recovered citations become RECOVERED (ARCHIVED), preserving the record of the original disappearance.
      </p>

      {(data.critical_dossiers || []).length > 0 && (
        <div className="mt-3 border border-[#D92D20] bg-[#1a0e0c] p-3" data-testid="recovery-critical">
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#D92D20]">Critical dossiers</div>
          {data.critical_dossiers.map((c) => (
            <div key={c.slug} className="mt-1.5 font-mono text-[10px] text-[#E4DFD3]">
              • {c.title || c.slug}
              <span className="text-[#7B7168]"> — {c.broken} confirmed broken
                {c.evidence_review_required ? " · evidence review flagged" : ""}</span>
            </div>
          ))}
        </div>
      )}

      <Group title="Available archived copies" hint="verified snapshots ready for editor approval"
             items={data.available} testid="recovery-group-available">
        {data.available.map((it) => (
          <RecoveryItem key={`${it.slug}::${it.url}`} item={it}
                        token={token} userToken={userToken} onChanged={load} />
        ))}
      </Group>
      <Group title="Not yet searched" hint="run a Wayback Machine lookup"
             items={data.unchecked} testid="recovery-group-unchecked">
        {data.unchecked.map((it) => (
          <RecoveryItem key={`${it.slug}::${it.url}`} item={it}
                        token={token} userToken={userToken} onChanged={load} />
        ))}
      </Group>
      <Group title="No archive available" hint="no snapshot located — needs an editorial decision"
             items={data.no_archive} testid="recovery-group-noarchive">
        {data.no_archive.map((it) => (
          <RecoveryItem key={`${it.slug}::${it.url}`} item={it}
                        token={token} userToken={userToken} onChanged={load} />
        ))}
      </Group>
      <Group title="Awaiting review" hint="conflicting signals — human judgment required"
             items={data.awaiting_review} testid="recovery-group-awaiting">
        {data.awaiting_review.map((it) => (
          <div key={`${it.slug}::${it.url}`} className="border border-[#2D2A26] bg-[#131313] p-2.5 font-mono text-[10px] text-[#A39E93]">
            {it.source_title || it.url} <span className="text-[#7B7168]">· {it.topic_title}</span>
          </div>
        ))}
      </Group>
      <Group title="Completed recoveries" hint="auditable editorial history"
             items={data.completed} testid="recovery-group-completed">
        {data.completed.map((it) => (
          <div key={`${it.slug}::${it.url}`} className="border border-[#2D2A26] bg-[#131313] p-2.5"
               data-testid={`recovery-completed-${it.slug}`}>
            <div className="font-mono text-[10px] text-[#E4DFD3]">
              {it.source_title || it.url} <span className="text-[#7B7168]">· {it.topic_title}</span>
            </div>
            {it.recovery && (
              <div className="font-mono text-[8px] text-[#7B7168] mt-1">
                {it.recovery.action} by {it.recovery.editor} · {String(it.recovery.at || "").slice(0, 10)}
                {it.recovery.reason ? ` · "${it.recovery.reason}"` : ""}
              </div>
            )}
          </div>
        ))}
      </Group>
    </div>
  );
};

export default EvidenceRecoveryQueue;
;
